import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { CreateReportModalComponent } from 'src/app/shared/components/molecules/create-report-modal/create-report-modal.component';
import {
  DeleteRequestProgramEvalModalComponent,
  TProgramEvaluationData,
  // eslint-disable-next-line
} from 'src/app/shared/components/molecules/delete-request-program-eval-modal/delete-request-program-eval-modal.component';
import {
  DialogModalComponent,
  DialogModalData,
} from 'src/app/shared/components/molecules/dialog-modal/dialog-modal.component';
import { NoticeModalComponent } from 'src/app/shared/components/molecules/notice-modal/notice-modal.component';
import { MatDialogResponse } from 'src/app/shared/models/constants/mat-dialog-response';
import { BannerResponseDTO } from 'src/app/shared/models/dtos/responses/banner-response-dto/banner-response-dto';

@Injectable({
  providedIn: 'root',
})
/**
 * Friendly dialog wrapper
 */
export class DialogService {
  dialogRef: any;
  screenWidth = window.innerWidth;
  /**
   * @constructor
   */
  constructor(private dialog: MatDialog) {}

  /**
   * Opens a modal and can execute actions depending on the response
   * @param {DialogModalData} dialogData
   * @param {Function} acceptAction what to do when modal is accepted
   * @param {Function} failAction what to do when modal is refused
   * @param {number} maxWidth
   * @param {number} hasDisclaimer
   */
  openDialog(
    dialogData: DialogModalData,
    acceptAction?: (response?: string) => void,
    failAction?: () => void,
    maxWidth?: number
  ) {
    const dialogConfig: MatDialogConfig = {};
    dialogData.hasDisclaimer = false;
    dialogConfig.data = dialogData;
    dialogConfig.autoFocus = false;
    dialogConfig.disableClose = true;

    if (maxWidth) {
      dialogConfig.maxWidth = maxWidth;
      dialogConfig.width = 'calc(100% - 20px)';
    }
    // eslint-disable-next-line security/detect-non-literal-fs-filename
    const dialogRef = this.dialog.open(DialogModalComponent, dialogConfig);

    dialogRef
      .afterClosed()
      .subscribe((dialogResponse: MatDialogResponse | string) => {
        if (acceptAction) {
          if (dialogResponse === MatDialogResponse.SUCCESS) {
            acceptAction();
            return;
          } else if (typeof dialogResponse === 'string') {
            acceptAction(dialogResponse);
            return;
          }
        }
        if (dialogResponse === MatDialogResponse.FAIL && failAction) {
          failAction();
        }
      });
    this.dialogRef = dialogRef;
  }

  /**
   * Opens a create a report modal
   * @param {number} familyId
   */
  openCreateReportModal(familyId: number) {
    const dialogRef = this.dialog.open(CreateReportModalComponent, {
      autoFocus: false,
      data: {
        familyId: familyId,
      },
    });
    this.dialogRef = dialogRef;
  }

  /**
   * @param {TProgramEvaluationData} programEvaluationData
   * @param {Function} acceptAction what to do when modal is accepted
   * @param {Function} failAction what to do when modal is refused
   * Opens a Delete Request Program Evaluation modal
   */
  openDeleteRequestProgramEvaluationModal(
    programEvaluationData: TProgramEvaluationData,
    acceptAction?: () => void,
    failAction?: () => void
  ) {
    // eslint-disable-next-line security/detect-non-literal-fs-filename
    const dialogRef = this.dialog.open(DeleteRequestProgramEvalModalComponent, {
      data: programEvaluationData,
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe((sendDelete: boolean) => {
      if (sendDelete && acceptAction) {
        acceptAction();
      } else if (sendDelete && failAction) {
        failAction();
      }
    });
    this.dialogRef = dialogRef;
  }

  /**
   * Opens a create a report modal
   * @param {BannerResponseDTO} bannerInfo
   */
  openNoticeModal(bannerInfo: BannerResponseDTO) {
    const dialogRef = this.dialog.open(NoticeModalComponent, {
      autoFocus: false,
      maxWidth: 800,
      width: this.setMaxWidth(),
      data: bannerInfo,
    });
    this.dialogRef = dialogRef;
  }

  /**
   * @return {string}
   */
  private setMaxWidth(): string {
    return this.screenWidth <= 620 ? '100%' : '50%';
  }

  /**
   * Closes current open dialog
   */
  closeCurrentDialog() {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }
}
